// src/components/FinishLynx/TrackLifFilesManagerFinal.js

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button
} from '@mui/material';
import {
  UploadFile as UploadFileIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircleOutline as CheckIcon,
  ErrorOutline as ErrorIcon,
  Flag as FlagIcon,
  Sports as SportsIcon
} from '@mui/icons-material';

// Importar contexto de evento
import { useEventContext } from '../../context/EventContext'; // Ajusta la ruta según tu estructura
import { useAuth } from '../../context/AuthContext'; // Ajusta la ruta según tu estructura

// Importaciones de Firebase
import { storage } from '../../firebase'; // Ajusta la ruta según tu estructura
import { 
  ref, 
  listAll, 
  getDownloadURL, 
  uploadBytesResumable, 
  deleteObject,
  getMetadata
} from 'firebase/storage';

// Componente optimizado final con los cambios solicitados
export default function TrackLifFilesManagerFinal() {
  // Obtener competitionId del contexto como en EventManagerFiles
  const {
    competitionId,
    loading: contextLoading
  } = useEventContext();
  
  const { user } = useAuth();

  // Estado para almacenar todos los archivos y paginación
  const [allFiles, setAllFiles] = useState([]);
  const [paginatedFiles, setPaginatedFiles] = useState([]);
  const [expandedFile, setExpandedFile] = useState(null);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  
  // Estados de carga y acciones
  const [loading, setLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDeleting, setIsDeleting] = useState(null);
  const [isLoadingContent, setIsLoadingContent] = useState({});
  
  // Estados para mensajes y diálogos (similar a EventManagerFiles)
  const [actionError, setActionError] = useState(null);
  const [actionSuccess, setActionSuccess] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState({ id: null, name: '' });
  
  // Estado para el contenido de los archivos y metadatos extraídos
  const [fileContents, setFileContents] = useState({});
  const [fileMetadata, setFileMetadata] = useState({});

  // Verificar si tenemos un ID de competición válido
  useEffect(() => {
    if (!competitionId) {
      console.error('No se proporcionó un ID de competición válido');
      setActionError('Error: No hay ID de competición proporcionado');
    }
  }, [competitionId]);

  // Manejar cambio de página
  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  // Manejar cambio de filas por página
  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  // Actualizar los archivos paginados cuando cambia la página o los archivos
  useEffect(() => {
    if (allFiles.length === 0) {
      setPaginatedFiles([]);
      setTotalPages(1);
      return;
    }
    
    const startIndex = (page - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    const filesForPage = allFiles.slice(startIndex, endIndex);
    
    setPaginatedFiles(filesForPage);
    setTotalPages(Math.ceil(allFiles.length / rowsPerPage));
  }, [allFiles, page, rowsPerPage]);

  // Función para extraer metadatos de un archivo LIF
  const parseLifFile = (content, fileName) => {
    if (!content || typeof content !== 'string') {
      return {
        eventName: 'Desconocido',
        windReading: null,
        hasWind: false
      };
    }
    
    // Dividir por líneas y detectar el separador (coma o tabulador)
    const lines = content.split('\n').filter(line => line.trim());
    if (lines.length === 0) return {};

    const separator = lines[0].includes(',') ? ',' : '\t';
    
    // Parsear la primera línea para obtener información del evento
    const headerParts = lines[0].split(separator);
    
    let eventName = 'Desconocido';
    let windReading = null;
    let hasWind = false;
    
    // Extraer información del encabezado
    if (headerParts.length >= 4) {
      // Formato típico: T10,1,1,Male 1500m,,,,,,,14:26:45.8053
      eventName = headerParts[3] || 'Desconocido';
      
      // Buscar información de viento (podría estar en diferentes posiciones)
      const windFields = headerParts.slice(4).filter(field => 
        field && field.trim() && !isNaN(parseFloat(field)) && 
        Math.abs(parseFloat(field)) < 10 // Típicamente los valores de viento están entre -9.9 y 9.9
      );
      
      if (windFields.length > 0) {
        windReading = parseFloat(windFields[0]);
        hasWind = true;
      }
    }
    
    return {
      eventName,
      windReading,
      hasWind
    };
  };

  // Cargar archivos desde Firebase Storage
  const fetchFiles = useCallback(async () => {
    // Evitar bucles infinitos verificando condiciones de error conocidas
    if (loading) return;
    
    if (!user?.uid) {
      setActionError('No hay usuario autenticado');
      return;
    }
    
    // Asegurarse de que existe un ID de competición válido
    if (!competitionId) {
      setActionError('No hay ID de competición válido');
      return;
    }
    
    setLoading(true);
    try {
      // Revisar si existe un subdirectorio "SistemaLynx" o variantes
      let basePath;
      try {
        const topPath = `usuarios/${user.uid}/competencias/${competitionId}`;
        const topRef = ref(storage, topPath);
        const topResult = await listAll(topRef);
        
        // Buscar carpeta SistemaLynx o variantes (ignorar mayúsculas/minúsculas)
        const lynxFolder = topResult.prefixes.find(prefix => 
          prefix.name.toLowerCase() === 'sistemalynx'
        );
        
        if (lynxFolder) {
          basePath = lynxFolder.fullPath;
        } else {
          // Si no hay carpeta específica, usar la carpeta principal
          basePath = topPath;
        }
      } catch (error) {
        // Si hay error al buscar subcarpetas, usar la ruta estándar
        basePath = `usuarios/${user.uid}/competencias/${competitionId}/SistemaLynx`;
      }
      
      // Crear referencia a la carpeta en Storage
      const lifFilesRef = ref(storage, basePath);
      
      // Obtener lista de archivos
      const result = await listAll(lifFilesRef);
      
      // Filtrar solo archivos .lif
      const lifItems = result.items.filter(item => item.name.toLowerCase().endsWith('.lif'));
      
      if (lifItems.length === 0) {
        setAllFiles([]);
        setActionSuccess('No se encontraron archivos LIF en la competición');
        setLoading(false);
        return;
      }
      
      // Procesar cada archivo
      const filesPromises = lifItems.map(async (item) => {
        const fileName = item.name;
        let downloadURL = '';
        
        try {
          downloadURL = await getDownloadURL(item);
        } catch (err) {
          console.error(`Error obteniendo URL de ${fileName}:`, err);
        }
        
        let metadata = { timeCreated: new Date().toISOString() };
        try {
          metadata = await getMetadata(item);
        } catch (err) {
          console.error(`Error obteniendo metadata de ${fileName}:`, err);
        }
        
        // Extraer información del nombre del archivo (formato típico: Txx-y-zz.lif - Descripción)
        let eventName = '';
        
        if (fileName.includes('-')) {
          const parts = fileName.split('-');
          if (parts.length > 3) {
            eventName = parts.slice(3).join('-').replace('.lif', '').trim();
          }
        }
        
        return {
          id: metadata.generation || fileName,
          fileName,
          storageFullPath: item.fullPath,
          downloadURL,
          eventName: eventName || 'Evento sin nombre',
          eventDate: metadata.timeCreated,
          uploadDate: metadata.timeCreated,
          status: 'Pendiente',
          hasWind: false,
          windReading: null
        };
      });
      
      const files = await Promise.all(filesPromises);
      
      // Ordenar archivos por fecha de subida (más recientes primero)
      files.sort((a, b) => new Date(b.uploadDate) - new Date(a.uploadDate));
      
      setAllFiles(files);
      
      // Limpiar contenidos previos
      setFileContents({});
      setFileMetadata({});
      setExpandedFile(null);
      
      // Mensaje de éxito
      setActionSuccess(`${files.length} archivo(s) cargado(s)`);
    } catch (error) {
      console.error('Error al cargar archivos LIF:', error);
      setActionError(`Error: ${error.message}`);
      setAllFiles([]);
    } finally {
      setLoading(false);
    }
  }, [user, competitionId]);

  // Cargar archivos al montar el componente y cuando cambie competitionId
  useEffect(() => {
    if (user?.uid && competitionId) {
      fetchFiles();
    }
  }, [user, competitionId, fetchFiles]);

  // Precargar contenido de los archivos visibles para extraer metadatos
  useEffect(() => {
    const preloadFileContents = async () => {
      if (paginatedFiles.length === 0 || loading) return;
      
      // Solo precargar archivos que no se han cargado aún
      const filesToLoad = paginatedFiles.filter(file => 
        !fileContents[file.id] && file.downloadURL && !isLoadingContent[file.id]
      );
      
      if (filesToLoad.length === 0) return;
      
      // Cargar el contenido de cada archivo
      for (const file of filesToLoad) {
        setIsLoadingContent(prev => ({ ...prev, [file.id]: true }));
        
        try {
          // Descargar el contenido del archivo
          const response = await fetch(file.downloadURL);
          if (!response.ok) throw new Error('Error al descargar el archivo');
          
          // Obtener el contenido como ArrayBuffer para manejar la codificación
          const buffer = await response.arrayBuffer();
          
          // Intentar decodificar con diferentes encodings
          let content;
          try {
            // Intento 1: Windows-1252 (ANSI) para archivos Windows
            content = new TextDecoder('windows-1252').decode(buffer);
          } catch (e) {
            try {
              // Intento 2: Latin1 (ISO-8859-1)
              content = new TextDecoder('iso-8859-1').decode(buffer);
            } catch (e2) {
              // Fallback: UTF-8
              content = new TextDecoder('utf-8').decode(buffer);
            }
          }
          
          // Normalizar saltos de línea CRLF a LF
          content = content.replace(/\r\n/g, '\n');
          
          // Actualizar el estado con el contenido
          setFileContents(prev => ({
            ...prev,
            [file.id]: content
          }));
          
          // Parsear metadatos del contenido
          const meta = parseLifFile(content, file.fileName);
          
          // Actualizar el estado con los metadatos
          setFileMetadata(prev => ({
            ...prev,
            [file.id]: meta
          }));
        } catch (error) {
          console.error(`Error al precargar archivo ${file.fileName}:`, error);
        } finally {
          setIsLoadingContent(prev => ({ ...prev, [file.id]: false }));
        }
      }
    };
    
    preloadFileContents();
  }, [paginatedFiles, fileContents, loading, isLoadingContent]);

  // Manejar la subida de archivos
  const handleFileUpload = async (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    event.target.value = null;
    
    setActionError(null);
    setActionSuccess(null);
    
    if (!user?.uid) {
      setActionError('Error: No hay usuario autenticado');
      return;
    }
    
    // Asegurarse de que existe un ID de competición válido
    if (!competitionId) {
      setActionError('Error: No hay ID de competición válido');
      return;
    }
    
    setIsUploading(true);
    setUploadProgress(0);
    
    try {
      for (const file of Array.from(files)) {
        if (!file.name.toLowerCase().endsWith('.lif')) {
          console.warn(`${file.name} no es un archivo LIF válido`);
          continue;
        }
        
        // Crear carpeta SistemaLynx si no existe
        const path = `usuarios/${user.uid}/competencias/${competitionId}/SistemaLynx/${file.name}`;
        const storageRef = ref(storage, path);
        
        // Subir archivo con progreso
        const uploadTask = uploadBytesResumable(storageRef, file);
        
        // Manejar progreso y obtener URL
        await new Promise((resolve, reject) => {
          uploadTask.on('state_changed',
            (snapshot) => {
              const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
              setUploadProgress(progress);
            },
            (error) => {
              reject(error);
            },
            async () => {
              try {
                await getDownloadURL(uploadTask.snapshot.ref);
                resolve();
              } catch (err) {
                reject(err);
              }
            }
          );
        });
      }
      
      // Recargar archivos después de subir
      await fetchFiles();
      
      // Ir a la primera página para ver los archivos recién subidos
      setPage(1);
      
      // Mensaje de éxito
      setActionSuccess(`Archivo(s) subido(s) correctamente`);
    } catch (error) {
      console.error('Error al subir archivos:', error);
      setActionError(`Error: ${error.message}`);
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  // Abrir diálogo de confirmación (similar a EventManagerFiles)
  const openConfirm = (id, name) => {
    setDeleteTarget({ id, name });
    setConfirmOpen(true);
  };

  // Cerrar diálogo de confirmación
  const closeConfirm = () => {
    setConfirmOpen(false);
    setDeleteTarget({ id: null, name: '' });
  };

  // Confirmar eliminación
  const handleConfirmDelete = async () => {
    closeConfirm();
    const fileToDelete = allFiles.find(f => f.id === deleteTarget.id);
    if (!fileToDelete) return;
    
    setIsDeleting(fileToDelete.id);
    
    try {
      // Eliminar de Storage
      if (fileToDelete.storageFullPath) {
        const fileRef = ref(storage, fileToDelete.storageFullPath);
        await deleteObject(fileRef);
      } else {
        // Si no hay ruta completa, construirla
        const path = `usuarios/${user.uid}/competencias/${competitionId}/SistemaLynx/${fileToDelete.fileName}`;
        const fileRef = ref(storage, path);
        await deleteObject(fileRef);
      }
      
      // Actualizar estado local
      setAllFiles(prev => prev.filter(f => f.id !== fileToDelete.id));
      
      // Limpiar contenido y metadatos si existen
      if (fileContents[fileToDelete.id]) {
        setFileContents(prev => {
          const newContents = { ...prev };
          delete newContents[fileToDelete.id];
          return newContents;
        });
        
        setFileMetadata(prev => {
          const newMeta = { ...prev };
          delete newMeta[fileToDelete.id];
          return newMeta;
        });
      }
      
      // Ajustar página si es necesario
      if (paginatedFiles.length === 1 && page > 1) {
        setPage(page - 1);
      }
      
      setActionSuccess(`${fileToDelete.fileName} eliminado.`);
    } catch (error) {
      console.error('Error al eliminar el archivo:', error);
      setActionError(`Error eliminando: ${error.message}`);
    } finally {
      setIsDeleting(null);
    }
  };

  // Manejar la expansión de acordeones
  const handleAccordionChange = (fileId) => (event, isExpanded) => {
    setExpandedFile(isExpanded ? fileId : null);
  };

  // Formatear fecha
  const formatDateTime = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = dateStr instanceof Date ? dateStr : new Date(dateStr);
    return date.toLocaleString();
  };

  // Obtener la información más precisa (metadatos del contenido o metadata inicial)
  const getFileInfo = (file) => {
    const meta = fileMetadata[file.id];
    
    if (meta) {
      return {
        eventName: meta.eventName || file.eventName || 'Evento sin nombre',
        hasWind: meta.hasWind || false,
        windReading: meta.windReading
      };
    }
    
    return {
      eventName: file.eventName || 'Evento sin nombre',
    };
  };

  return (
    <Box>
      {/* Header con título y acciones */}
      <Box mb={2} display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="h6">
          Archivos LIF de FinishLynx
          {allFiles.length > 0 && ` (${allFiles.length})`}
        </Typography>
        
        {/* Botones de acción simplificados a solo iconos */}
        <Box display="flex" gap={1}>
          <Tooltip title="Refrescar archivos">
            <IconButton
              color="primary"
              onClick={fetchFiles}
              disabled={contextLoading || loading}
              size="small"
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          
          <Tooltip title="Subir archivos LIF">
            <IconButton
              color="primary"
              component="label"
              disabled={isUploading || !competitionId || contextLoading}
              size="small"
            >
              {isUploading ? <CircularProgress size={20} /> : <UploadFileIcon />}
              <input
                type="file"
                hidden
                multiple
                accept=".lif"
                onChange={handleFileUpload}
              />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Selector de filas por página */}
      {allFiles.length > 10 && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <FormControl size="small" sx={{ width: 120 }}>
            <InputLabel>Mostrar</InputLabel>
            <Select
              value={rowsPerPage}
              label="Mostrar"
              onChange={handleRowsPerPageChange}
            >
              <MenuItem value={5}>5 archivos</MenuItem>
              <MenuItem value={10}>10 archivos</MenuItem>
              <MenuItem value={25}>25 archivos</MenuItem>
              <MenuItem value={50}>50 archivos</MenuItem>
              <MenuItem value={100}>100 archivos</MenuItem>
            </Select>
          </FormControl>
        </Box>
      )}

      {/* Progreso de carga */}
      {isUploading && (
        <Box sx={{ mb: 2 }}>
          <LinearProgress variant="determinate" value={uploadProgress} sx={{ height: 3 }} />
          <Typography variant="caption" textAlign="right">{uploadProgress}%</Typography>
        </Box>
      )}

      {/* Indicador de carga */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Lista de archivos paginados */}
      {paginatedFiles.length > 0 ? (
        <>
          <Box>
            {paginatedFiles.map((file) => {
              const fileInfo = getFileInfo(file);
              const isLoading = isLoadingContent[file.id];
              
              return (
                <Accordion
                  key={file.id}
                  expanded={expandedFile === file.id}
                  onChange={handleAccordionChange(file.id)}
                  sx={{ mb: 1 }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="subtitle1">
                          {file.fileName}
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', mt: 0.5, gap: 0.5 }}>
                          {/* Evento con información del archivo parseado */}
                          <Chip
                            size="small"
                            icon={<SportsIcon />}
                            label={`Evento: ${fileInfo.eventName}`}
                            color="primary"
                            variant="outlined"
                          />
                          
                          {/* Fecha de subida */}
                          <Chip
                            size="small"
                            label={formatDateTime(file.uploadDate)}
                            variant="outlined"
                          />
                          
                          {/* Indicador de carga */}
                          {isLoading && (
                            <CircularProgress size={16} sx={{ ml: 1 }} />
                          )}
                        </Box>
                      </Box>
                      
                      {/* Acciones rápidas */}
                      <Box sx={{ ml: 1, display: 'flex', gap: 1 }}>
                        <Tooltip title="Descargar">
                          <IconButton
                            size="small"
                            href={file.downloadURL}
                            target="_blank"
                            disabled={!file.downloadURL || contextLoading}
                          >
                            <DownloadIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={(e) => {
                              e.stopPropagation();
                              openConfirm(file.id, file.fileName);
                            }}
                            disabled={isDeleting === file.id || contextLoading}
                          >
                            {isDeleting === file.id ? (
                              <CircularProgress size={20} />
                            ) : (
                              <DeleteIcon />
                            )}
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  </AccordionSummary>
                  
                  <AccordionDetails>
                    {/* Mostrar contenido del archivo */}
                    {isLoading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress />
                      </Box>
                    ) : (
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 2,
                          fontFamily: 'monospace',
                          whiteSpace: 'pre-wrap',
                          overflow: 'auto',
                          maxHeight: '400px',
                          fontSize: '0.9rem',
                          backgroundColor: '#f8f9fa'
                        }}
                      >
                        {fileContents[file.id] || 'Cargando contenido...'}
                      </Paper>
                    )}
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Box>
          
          {/* Controles de paginación */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Pagination 
                count={totalPages} 
                page={page} 
                onChange={handlePageChange} 
                color="primary" 
              />
            </Box>
          )}
        </>
      ) : (
        <Paper
          variant="outlined"
          sx={{
            p: 3,
            textAlign: 'center',
            backgroundColor: '#f5f5f5'
          }}
        >
          <Typography variant="body1" color="text.secondary">
            {loading ? 'Cargando...' : 'No hay archivos LIF disponibles en esta competición'}
          </Typography>
        </Paper>
      )}

      {/* Mensajes de acción (similar a EventManagerFiles) */}
      <Box mt={2} minHeight={48}>
        {actionError && (
          <Alert 
            severity="error" 
            icon={<ErrorIcon />} 
            onClose={() => setActionError(null)}
          >
            {actionError}
          </Alert>
        )}
        {actionSuccess && (
          <Alert 
            severity="success" 
            icon={<CheckIcon />} 
            onClose={() => setActionSuccess(null)}
          >
            {actionSuccess}
          </Alert>
        )}
      </Box>

      {/* Diálogo de confirmación (similar a EventManagerFiles) */}
      <Dialog open={confirmOpen} onClose={closeConfirm}>
        <DialogTitle>Confirmar eliminación</DialogTitle>
        <DialogContent>
          <Typography>¿Eliminar <strong>{deleteTarget.name}</strong>?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirm}>Cancelar</Button>
          <Button onClick={handleConfirmDelete} variant="contained" color="error">Eliminar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}